import {
    AutocompleteInputChangeReason,
    AutocompleteProps,
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    Autocomplete as MuiAutocomplete,
    OutlinedInputProps,
    Paper,
    Popper,
    PopperProps,
    Tooltip,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import { Instance } from '@popperjs/core';
import clsx from 'clsx';
import { t } from 'i18next';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TEntity } from '../../../../models/entity';
import { markRequiredLabelRed, showRequiredMark } from '../../../utils/formUtils';
import { usePlainSearch, useResizeObserver } from '../../../utils/utilHooks';
import { useEditPanelContext } from '../../components/AdminDataGrid/EditPanelContext';
import { EditorModus } from '../../components/EditDialog/EditDialog';
import { CheckBoxOutlinedBlankIcon, CheckBoxOutlinedIcon, CloseIcon, } from '../../controls/MaterialUIIcons';
import { redRequiredLabel } from '../TextField/TextField';

export type CustomAutocompleteProps<T,
    Multiple extends boolean | undefined = undefined> =
    Omit<AutocompleteProps<T, Multiple, boolean | undefined, boolean | undefined>,
        'renderInput' | 'options' | 'getOptionLabel'>
    & {
        error?: any;
        required?: boolean;
        label?: string;
        validateFunction?: (fieldValue: any, key: string) => boolean | undefined;
        disableValidation?: boolean;
        options?: T[];
        getOptions: (searchString: string) => Promise<T[]>;
        getOptionLabel(option: T): string;
        getPopperOptionLabel?(option: T): string | ReactElement;
        className?: string;
        name: string;
        emptyOption?: boolean;
        disabled?: boolean;
        disableAdd?: boolean;
        disableRemove?: boolean;
        minimumSearchLength?: number;
        fullWidth?: boolean;
        isShowErrorTooltip?: boolean;
        checkboxFilterProps?: CheckboxFilterProps<T>;
        getOptionBoxKey?: (option: T) => string;
        InputProps?: Partial<OutlinedInputProps>;
        mode?: EditorModus;
    };

type CheckboxFilterProps<T> = {
    checkboxFilter: (option: T) => boolean;
    label: string;
    disableFiltered?: boolean;
};

export const useAutocompleteStyles = makeStyles((theme) => ({
    autocompleteMultiselectStyle: {
        '& .MuiInputBase-root ': {
            'padding-right': '16px !important'
        },
    },
    autocompleteStyle: {
        '& .MuiAutocomplete-input': {
            'padding-right': '28px !important'
        }
    }
}));

export type MultiSelectAutocompleteProps<T> = Omit<CustomAutocompleteProps<T, true>,
    | 'renderOption'
    | 'renderInput'
    | 'multiple'
    | 'disableCloseOnSelect'
    | 'getOptionLabel'
    | 'onChange'
    | 'options'> & {
        label: string;
        options?: T[];
        required?: boolean;
        singleton?: boolean; // select only one option
        onChange(evt: { target: { name: string; value: T[] } }): void;
        transformSelectedValue?(value: T[]): any;
        getOptionLabel(option: T): string;
    };

const icon = <CheckBoxOutlinedBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxOutlinedIcon fontSize="small" />;

const PaperComponent = React.forwardRef<'Paper',
    any & {
        checkboxProps?: {
            label: string;
            checked: boolean;
            setChecked: (val: boolean) => void;
        };
    }>(function Component(props, ref) {
        const { children, checkboxProps, ...other } = props;
        const label = checkboxProps?.label;
        const checked = checkboxProps?.checked;
        const setChecked = checkboxProps?.setChecked;

        return (
            <Paper {...other} ref={ref} style={{
                ...other.style,
                border: 'rgba(0, 0, 0, 0.2) 1.5px solid',
                borderRadius: '4px'
            }}>
                {children}
                {checkboxProps && (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.2)' }} />
                        <FormControlLabel
                            style={{ marginLeft: '.5rem' }}
                            control={
                                <Checkbox
                                    checked={checked}
                                    onMouseDownCapture={(evt: any) => {
                                        evt.defaultMuiPrevented = false;
                                        setChecked(!checked);
                                        evt.preventDefault();
                                        evt.stopPropagation();
                                    }}
                                />
                            }
                            label={label ?? ''}
                            labelPlacement={'end'}
                        />
                    </div>
                )}
            </Paper>
        );
    });


export default function Autocomplete<T, Multiple extends boolean | undefined>(
    props: CustomAutocompleteProps<T, Multiple>
) {
    const {
        fullWidth = true,
        disablePortal = true,
        options: inputOptions,
        disableValidation,
        getPopperOptionLabel = props.getOptionLabel,
        isOptionEqualToValue = (option, value) => option === value,
        getOptions,
        error = null,
        validateFunction,
        isShowErrorTooltip = false,
        label,
        disableAdd = false,
        minimumSearchLength = 0,
        onInputChange: baseOnInputChange,
        name,
        required,
        filterOptions,
        emptyOption = !required,
        title,
        disabled,
        getOptionBoxKey = (opt: any) => opt?.id,
        checkboxFilterProps,
        InputProps,
        mode,
        ...other
    } = props;

    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<((T & { filtered?: boolean }) | T)[]>(inputOptions ?? []);
    const [checked, setChecked] = useState(false);

    const setOptionsCallback = useCallback(
        (loadedOptions: T[]) => {
            if (!checkboxFilterProps?.checkboxFilter) setOptions(loadedOptions);
            else
                setOptions(
                    loadedOptions.map((opt) => ({
                        ...opt,
                        filtered: !checkboxFilterProps.checkboxFilter?.(opt),
                    }))
                );
        },
        [checkboxFilterProps]
    );

    const { loading, searchString, setSearchString } = usePlainSearch({
        minimumSearchLength,
        setOptions: setOptionsCallback,
        getOptions,
        open,
    });

    function onInputChange(
        evt: React.SyntheticEvent<Element, Event>,
        value: string,
        reason: AutocompleteInputChangeReason
    ) {
        if (reason !== 'reset') {
            setSearchString(value);
            baseOnInputChange?.(evt, value, reason);
        }
    }

    useEffect(() => {
        setSearchString('');
    }, [open, setSearchString]);

    const { validateField, mode: contextMode } = useEditPanelContext<TEntity>();
    const handleLoseFocus = useCallback(
        (e: React.FocusEvent<HTMLDivElement, Element>) => {
            other.onBlur?.(e);
            if ((!validateField && !validateFunction) || !required) return;
            if (validateField) validateField(props.value, name);
            if (validateFunction) validateFunction(props.value, name);
        },
        [other, validateField, validateFunction, required, props.value, name]
    );

    function getOptionsWithValue() {
        if (!open && Array.isArray(props.value)) return props.value;
        if (!open && props.value) return [props.value as T];
        if ((emptyOption && !filterOptions))
            return [{} as T, ...options];
        return options;
    }

    const currOptions = getOptionsWithValue();
    const inputRef = useRef<HTMLInputElement>(null);
    const { autocompleteStyle } = useAutocompleteStyles();
    const { requiredLabelStyle } = redRequiredLabel();
    const noOptionsText = useMemo(() => {
        if (loading) return t('Laster...');
        if (searchString === null || searchString?.length < minimumSearchLength || searchString?.length === 0 || searchString === '')
            return t('Skriv inn minst {{minimumSearchLength}} tegn', { minimumSearchLength });
        return t('Ingen resultater');
    }, [loading, searchString, minimumSearchLength]);
    return (
        <MuiAutocomplete
            {...other}
            disablePortal={disablePortal}
            disabled={mode === EditorModus.Vis || contextMode === EditorModus.Vis || disabled}
            fullWidth={fullWidth}
            getOptionLabel={(opt: any) => props.getOptionLabel(opt) ?? ''}
            onOpen={(evt) => {
                if (!disableAdd) setOpen(true);
            }}
            loadingText={t('Laster...')}
            noOptionsText={noOptionsText}
            onClose={(prop) => setOpen(false)}
            renderTags={(option: T[]) => {
                if (!option?.length)
                    return (<div></div>);
                return (<div>
                    {
                        option.slice(1).reduce((previousValue: string, currentValue: T) => {
                            return previousValue + `, ${props.getOptionLabel(currentValue)}`;
                        }, props.getOptionLabel(option[0]))
                    }
                </div>);
            }}
            disableClearable={true}
            filterOptions={filterOptions}
            open={open}
            onBlur={(e) => handleLoseFocus(e)}
            isOptionEqualToValue={isOptionEqualToValue}
            openText=""
            closeText=""
            componentsProps={{
                ...props.componentsProps,
                paper: {
                    sx: {
                        '& .MuiAutocomplete-listbox': {
                            'maxHeight': '20vh !important'
                        }
                    }
                }
            }}
            options={
                checked ? currOptions : currOptions.filter((i) => !i?.filtered)
            }
            PopperComponent={props => <ResponsivePopper {...props} inputRef={inputRef} />}
            loading={loading}
            onInputChange={onInputChange}
            PaperComponent={(props) => (
                <PaperComponent
                    {...props}
                    checkboxProps={
                        checkboxFilterProps != null
                            ? {
                                label: checkboxFilterProps.label,
                                setChecked,
                                checked,
                            }
                            : undefined
                    }
                />
            )}
            getOptionDisabled={(opt: T & { filtered?: boolean }) =>
                other.getOptionDisabled?.(opt) ||
                (checkboxFilterProps?.disableFiltered! && opt.filtered!)
            }
            renderOption={(params: any, option: T & { filtered?: boolean }, state) => {
                const label = getPopperOptionLabel(option);
                return (
                    <Box
                        component="li"
                        {...params}
                        key={getOptionBoxKey?.(option) ?? params.key}
                        color={option?.filtered ? 'rgba(0, 0, 0, 0.55)' : undefined}
                    >
                        {
                            (typeof (label) !== 'string' || label?.length > 0) ? (
                                label
                            ) : (
                                <Box display="block" minHeight={'1em'} key={'empty_opt'}></Box>
                            )}
                    </Box>
                );
            }}
            renderInput={(params) => {
                return (
                    <Tooltip title={isShowErrorTooltip ? error ?? '' : title ?? ''}>
                        <TextField
                            {...params}
                            ref={inputRef}
                            disabled={
                                ((mode === EditorModus.Vis || contextMode === EditorModus.Vis) && !disableValidation) || disabled
                            }
                            required={!disableValidation && showRequiredMark(other.value, mode, error, required)}
                            error={!!error}
                            label={label}
                            className={clsx(other.className, autocompleteStyle, InputProps?.className, markRequiredLabelRed(other.value, mode ?? contextMode) ? requiredLabelStyle : '')}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                ...InputProps,
                                ...params.InputProps,
                                startAdornment: InputProps?.startAdornment ?? params.InputProps.startAdornment,
                                readOnly: disableAdd,
                                fullWidth: fullWidth,
                                endAdornment: (
                                    <>
                                        {loading ? (
                                            <CircularProgress
                                                color="inherit"
                                                size={20}
                                                sx={{ mr: 0.5 }}
                                            />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    </Tooltip>
                );
            }}
            ChipProps={{
                variant: 'outlined',
                deleteIcon: (
                    <CloseIcon
                        color="secondary"
                        sx={{
                            width: props.disableClearable ? 0 : '1rem',
                            height: props.disableClearable ? 0 : '1rem',
                        }}
                    />
                ),
            }}
        />
    );
}

export function AutoCompleteMultiSelect<T extends object>(
    props: MultiSelectAutocompleteProps<T>
) {
    const {
        onChange,
        name,
        options,
        transformSelectedValue,
        validateFunction,
        singleton = false,
        required,
        ...others
    } = props;
    const convertToDefEventPara = (n: string, value: any) => {
        return ({
            target: {
                name: n,
                value: transformSelectedValue?.(value) ?? value,
            },
        });
    };
    const { autocompleteMultiselectStyle } = useAutocompleteStyles();
    const { validateField, mode } = useEditPanelContext<TEntity>();

    const handleLoseFocus = useCallback(
        (e: React.FocusEvent<HTMLDivElement, Element>) => {
            if ((!validateField && !validateFunction) || !required) return;
            if (validateFunction) validateFunction(props.value, name);
            if (validateField) validateField(props.value, name);

        },
        [validateField, validateFunction, required, name, props.value]
    );
    const { requiredLabelStyle } = redRequiredLabel();


    return (
        <Autocomplete<T, true>
            {...others}
            options={options ?? []}
            open={undefined}
            multiple
            disableClearable
            emptyOption={false}
            required={!props.disableValidation && showRequiredMark(others.value, mode, props.error, required)}
            onBlur={handleLoseFocus}
            className={clsx(autocompleteMultiselectStyle, markRequiredLabelRed(others.value, mode) ? requiredLabelStyle : '')}
            name={props.name}
            disableCloseOnSelect={!singleton}
            onChange={(e: any, value: any) =>
                onChange(convertToDefEventPara(name, value))
            }

            renderOption={(p, option, { selected }) => (
                <li {...p}>
                    <Checkbox
                        sx={{ mt: 0, mb: 0, pt: 0, pb: 0 }}
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                    />
                    {props.getOptionLabel(option)}
                </li>
            )}
        />
    );
}

interface ResponsivePopperProps extends PopperProps {
    inputRef: React.RefObject<HTMLDivElement | undefined>;
}

export function ResponsivePopper({ inputRef, ...props }: ResponsivePopperProps) {

    const popperRef = React.useRef<Instance>(null);

    useResizeObserver(inputRef, () => {
        if (props.open) popperRef?.current?.forceUpdate();
    });

    return (
        <Popper
            {...props}
            popperRef={popperRef}
            placement="bottom"
            modifiers={[
                {
                    name: 'flip',
                    enabled: false,
                },
                {
                    name: 'sameWidth',
                    enabled: true,
                    fn: ({ state }) => {
                        if (state.placement === 'top')
                            state.styles.popper.marginBottom = '8px';
                        state.styles.popper.width = `${state.rects.reference.width}px`;
                    },
                    phase: 'beforeWrite',
                    requires: ['computeStyles'],
                },
            ]}
        />);
}

